import { QuoteWidget } from "./widgets/quote-widget/quote-widget";
import { useStyle } from "./style";
import { HomeTableWidget } from "./widgets/home-table-widget/home-table-widget";
import { useHome } from "./use-home";
import { useEffect } from "react";
import { CardsWidget } from "./widgets/cards-widget/cards-widget";
import { Skeleton } from "@mui/material";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { homeReportsState } from "@/pages-components/quote-new/store/quote";
import { permissionsState } from "@/store/permissions";
import { PermissionCheck } from "@/components/CheckPermission";
import { Permissions } from "@/components/CheckPermission/enum";
import { useThemeComponent } from "@/contexts/theme";
import { useGomakeAxios } from "@/hooks";
import { getAllReportsApi } from "@/services/api-service/generic-doc/documents-api";
import { EStatisticsLabels } from "@/pages-components/quotes/enums";
import { userState } from "@/store";
import {
  getAllStuckJobsApi,
  getAllStuckJobsByAgentIdApi,
  getNumberOfJobsWaitingForApprovalApi,
  getNumberOfJobsWaitingForApprovalByAgentIdApi,
} from "@/services/api-service/production-floor/production-floor-endpoints";
import { getAgentTargetsApi } from "@/services/api-service/reports-service/sales-controller-endpoints";
import { useUserPermission } from "@/hooks/use-permission";
import { MenuItem, Select } from "@mui/material";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import {
  EHomeDateFilter,
  getHomeDateRange,
  homeDateFilterState,
} from "./home-date-filter";

/** Default (fallback) documents section: title on its own row, then table widget with balance in toolbar */
const DefaultHomeDocumentsSection = ({
  documentsTitle,
  clientName,
  clientId,
  clientBalance,
}: {
  documentsTitle: string;
  clientName?: string;
  clientId?: string;
  clientBalance?: number;
}) => {
  const { classes } = useStyle();
  return (
    <div style={classes.secondRowContainer}>
      <div style={classes.titleStyle}>
        {documentsTitle}{" "}
        <span
          style={{
            color: "var(--theme-text-primary-light, rgb(213, 214, 233))",
          }}
        >
          / {clientName}
        </span>
      </div>
      <div style={{ marginBottom: 20 }}>
        <HomeTableWidget
          clientId={clientId}
          clientBalance={clientBalance}
        />
      </div>
    </div>
  );
};

/** Default: no heading in the statistics header (the date selector sits alone at the end). */
const DefaultHomeStatsHeading = () => null;

const HomePageComponentForAdmin = ({ isMobileScreen }) => {
  const { classes } = useStyle();
  const { setIsDisplay, isDisplay, flag, selectedClient, t, Title } = useHome();
  const allReports = useRecoilValue<any>(homeReportsState);
  const setAllReports = useSetRecoilState<any>(homeReportsState);
  const { callApi } = useGomakeAxios();
  const permissions = useRecoilValue<any[]>(permissionsState);
  const { CheckPermission } = useUserPermission();

  const canCreateQuote = CheckPermission(Permissions.CREATE_QUOTES);

  const user = useRecoilValue<any>(userState);
  const [dateFilter, setDateFilter] = useRecoilState(homeDateFilterState);

  const viewOnlyMine =
    permissions.includes(Permissions.VIEW_ONLY_MY_JOBS_AND_ORDERS) ||
    !!user?.isAgent;

  const fetchCount = async (allApi, byAgentApi, data?): Promise<number> => {
    let count = 0;
    const callBack = (res) => {
      count = Number(res?.data) || 0;
    };
    if (viewOnlyMine) {
      await byAgentApi(callApi, callBack, data);
    } else {
      await allApi(callApi, callBack, data);
    }
    return count;
  };

  const getTargetForUser = async (): Promise<number> => {
    let targets: any[] = [];
    await getAgentTargetsApi(callApi, (res) => {
      if (res?.success) {
        targets = res?.data || [];
      }
    });
    if (!Array.isArray(targets) || targets.length === 0) {
      return 0;
    }
    if (viewOnlyMine && user?.userId) {
      const own = targets.find((tr) => tr?.agentId === user?.userId);
      return Number(own?.target) || 0;
    }
    // Admin / global view: sum all per-agent targets (incl. the No-Agent row)
    return targets.reduce((sum, tr) => sum + (Number(tr?.target) || 0), 0);
  };

  const getAllReports = async () => {
    const range = getHomeDateRange(dateFilter);
    const [stuckJobs, jobsWaiting, target] = await Promise.all([
      fetchCount(getAllStuckJobsApi, getAllStuckJobsByAgentIdApi, {
        fromDate: range.fromDate,
        toDate: range.toDate,
      }),
      fetchCount(
        getNumberOfJobsWaitingForApprovalApi,
        getNumberOfJobsWaitingForApprovalByAgentIdApi,
        { fromDate: range.fromDate, toDate: range.toDate }
      ),
      getTargetForUser(),
    ]);

    const callBack = (res) => {
      if (res?.success) {
        const baseReports = Array.isArray(res?.data) ? res.data : [];
        const ordersTotal =
          Number(
            baseReports.find((r) => r?.key === EStatisticsLabels.ORDERS_TOTAL)
              ?.value
          ) || 0;
        const targetPercentage =
          target > 0 ? Math.round((ordersTotal / target) * 100) : 0;

        setAllReports([
          { key: EStatisticsLabels.TARGET_PERCENTAGE, value: targetPercentage },
          ...baseReports,
          {
            key: EStatisticsLabels.JOBS_WAITING_FOR_APPROVAL,
            value: jobsWaiting,
          },
          { key: EStatisticsLabels.STUCK_JOBS, value: stuckJobs },
        ]);
      }
    };
    await getAllReportsApi(callApi, callBack, {
      fromDate: range.fromDate,
      toDate: range.toDate,
    } as any);
  };

  useEffect(() => {
    if (!canCreateQuote) {
      getAllReports();
    }
  }, [canCreateQuote, dateFilter]);


  const ThemedHomeDocumentsSection = useThemeComponent(
    "HomeDocumentsSection",
    DefaultHomeDocumentsSection
  );
  // Optional heading at the start of the statistics header (themes may supply one).
  const ThemedHomeStatsHeading = useThemeComponent(
    "HomeStatsHeading",
    DefaultHomeStatsHeading
  );

  useEffect(() => {
    setIsDisplay(flag);
  }, [selectedClient]);

  return (
    <div style={classes.mainContainer}>
      <div className="gm-home-first-row" style={classes.firstRowContainer}>
        <div
          style={
            isMobileScreen
              ? classes.mobileContainerStyle
              : classes.containerStyle
          }
        >
          {canCreateQuote && (
            <div style={classes.widgetStyle}>
              <QuoteWidget isMobileScreen={isMobileScreen} title={Title} />
            </div>
          )}

          {!isMobileScreen && (
            <div
              style={{
                ...classes.widgetStyle,
                minHeight: canCreateQuote ? "100%" : 320,
              }}
            >
              <PermissionCheck
                userPermission={Permissions.SHOW_HOME_PAGE_STATISTICS}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    height: 280,
                    boxSizing: "border-box",
                    padding: "12px 16px 16px",
                    gap: 0,
                    backgroundColor: "var(--theme-bg-primary, #FFFFFF)",
                    borderRadius: "var(--theme-radius-md, 8px)",
                    boxShadow:
                      "var(--theme-shadow-md, 0 1px 0px 0 rgba(0, 0, 0, 0.08), 0 0px 5px 0 rgba(0, 0, 0, 0.08))",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      width: "100%",
                      borderBottom:
                        "1px solid var(--Stroke-Decorative-Default, #EEEEEE)",
                      paddingBottom: 0,
                    }}
                  >
                    <ThemedHomeStatsHeading />
                    <div
                      className="gm-home-date"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "4px 8px",
                        borderRadius: 8,
                        cursor: "default",
                        color: "var(--theme-primary-color, #2e3092)",
                        marginInlineStart: "auto",
                      }}
                    >
                      <CalendarTodayRoundedIcon
                        style={{
                          fontSize: 18,
                          color: "var(--theme-primary-color, #2e3092)",
                        }}
                      />
                      <Select
                        variant="standard"
                        disableUnderline
                        value={dateFilter}
                        onChange={(e) =>
                          setDateFilter(e.target.value as EHomeDateFilter)
                        }
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "var(--theme-primary-color, #2e3092)",
                          "& .MuiSelect-select": {
                            padding: 0,
                            paddingRight: "24px !important",
                          },
                          "& .MuiSvgIcon-root": {
                            color: "var(--theme-primary-color, #2e3092)",
                          },
                        }}
                      >
                        <MenuItem value={EHomeDateFilter.Today}>
                          {t("dashboard.today")}
                        </MenuItem>
                        <MenuItem value={EHomeDateFilter.ThisWeek}>
                          {t("dashboard.thisWeek")}
                        </MenuItem>
                        <MenuItem value={EHomeDateFilter.ThisMonth}>
                          {t("dashboard.thisMonth")}
                        </MenuItem>
                        <MenuItem value={EHomeDateFilter.Last30Days}>
                          {t("dashboard.last30Days")}
                        </MenuItem>
                      </Select>
                    </div>
                  </div>
                  {allReports ? (
                    <div
                      style={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                      }}
                    >
                      <CardsWidget />
                    </div>
                  ) : (
                    <Skeleton
                      variant="rectangular"
                      sx={classes.skeltonStyle}
                    />
                  )}
                </div>
              </PermissionCheck>
            </div>
          )}
        </div>
      </div>
      {!isMobileScreen && isDisplay && (
        <ThemedHomeDocumentsSection
          documentsTitle={t("sales.quote.documents")}
          clientName={selectedClient?.name}
          clientId={selectedClient?.id}
          clientBalance={selectedClient?.clientBalance}
        />
      )}
    </div>
  );
};

export { HomePageComponentForAdmin };
